/**
 * Selection corrections panel — flag wrong staff/projects and where to look.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { AlertTriangle, Save, Users, Building2 } from 'lucide-react';
import {
  getSelectionCorrections,
  putSelectionCorrections,
  type CompanyCorrectionItem,
  type SelectionCorrectionsPayload,
  type StaffCorrectionItem,
} from '../../api/feedbackApi';
import { downloadReportV1, downloadReportV2, getReport } from '../../api/reports';
import { listEntities } from '../../api/entities';
import { useWorkspace } from '../../hooks/useWorkspace';
import type { SuggestedAssignment } from '../Reports/StaffAssignmentsTab';
import {
  mergeProposalStaffAssignments,
  parseProposalStaffAssignments,
} from '../../utils/parseProposalStaffAssignments';
import { formatStaffMatchSummary } from '../../utils/formatStaffMatchSummary';

type StaffCorrectionMode = 'none' | 'specific' | 'search';

/** Plain text in the UI; persisted as a single trimmed hint on save. */
function freeTextFromList(items: string[] | undefined): string {
  if (!items?.length) return '';
  return items.length === 1 ? items[0] : items.join(', ');
}

function freeTextToList(text: string): string[] {
  const trimmed = text.trim();
  return trimmed ? [trimmed] : [];
}

function commaSeparatedFromList(items: string[] | undefined): string {
  if (!items?.length) return '';
  if (items.length === 1 && items[0].includes(',')) return items[0];
  return items.join(', ');
}

function commaSeparatedToList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeStaffCorrections(items: StaffCorrectionItem[]): StaffCorrectionItem[] {
  return [...items]
    .map((row) => ({
      position_id: row.position_id,
      role_title: row.role_title || undefined,
      action: row.action || 'replace',
      assign_staff_member_id: row.assign_staff_member_id || undefined,
      reject_staff_member_id: row.reject_staff_member_id || undefined,
      reason: (row.reason ?? '').trim() || undefined,
      search_hints: (row.search_hints ?? []).map((hint) => hint.trim()).filter(Boolean),
    }))
    .sort((a, b) => a.position_id.localeCompare(b.position_id));
}

function normalizeCompanyCorrections(items: CompanyCorrectionItem[]): CompanyCorrectionItem[] {
  return [...items]
    .map((row) => ({
      bucket_id: row.bucket_id,
      action: row.action || 'replace_projects',
      reject_project_labels: (row.reject_project_labels ?? [])
        .map((label) => label.trim())
        .filter(Boolean),
      pin_entity_ids: [...(row.pin_entity_ids ?? [])].filter(Boolean).sort(),
      pin_artifact_ids: (row.pin_artifact_ids ?? [])
        .map((id) => id.trim())
        .filter(Boolean),
      search_hints: (row.search_hints ?? []).map((hint) => hint.trim()).filter(Boolean),
      reason: (row.reason ?? '').trim() || undefined,
    }))
    .sort((a, b) => a.bucket_id.localeCompare(b.bucket_id));
}

/** Saved corrections may only include edited roles — always show every template position. */
function mergeStaffDraftWithRequirements(
  requirements: StaffRequirement[],
  saved: StaffCorrectionItem[] | undefined,
): StaffCorrectionItem[] {
  const savedById = new Map((saved ?? []).map((row) => [row.position_id, row]));
  return requirements.map((req) => {
    const existing = savedById.get(req.position_id);
    if (existing) {
      return {
        ...existing,
        role_title: existing.role_title || req.role_title,
      };
    }
    return {
      position_id: req.position_id,
      role_title: req.role_title,
      action: 'replace',
      assign_staff_member_id: undefined,
      reason: '',
      search_hints: [],
    };
  });
}

function correctionModesFromStaffDraft(
  rows: StaffCorrectionItem[],
): Record<string, StaffCorrectionMode> {
  const modes: Record<string, StaffCorrectionMode> = {};
  for (const row of rows) {
    if ((row.search_hints?.length ?? 0) > 0 && !row.assign_staff_member_id) {
      modes[row.position_id] = 'search';
    } else if (row.assign_staff_member_id || row.reason?.trim()) {
      modes[row.position_id] = 'specific';
    } else {
      modes[row.position_id] = 'none';
    }
  }
  return modes;
}

function resolveStaffMemberIdByName(
  name: string,
  staffMembers: StaffMemberOption[],
): string | undefined {
  const norm = name.trim().toLowerCase();
  if (!norm) return undefined;
  const exact = staffMembers.find((m) => m.name.toLowerCase() === norm);
  if (exact) return exact.id;
  const prefix = norm.split(',')[0]?.trim() ?? norm;
  const byPrefix = staffMembers.find((m) => {
    const memberName = m.name.toLowerCase();
    return memberName === prefix || memberName.startsWith(prefix) || prefix.startsWith(memberName);
  });
  if (byPrefix) return byPrefix.id;
  const partial = staffMembers.find((m) => {
    const memberName = m.name.toLowerCase();
    return norm.includes(memberName) || memberName.includes(prefix);
  });
  return partial?.id;
}

function resolveProposalStaffMemberIds(
  proposalByPosition: Record<string, { name?: string; staff_member_id?: string }>,
  staffMembers: StaffMemberOption[],
): Record<string, { name?: string; staff_member_id?: string }> {
  const resolved: Record<string, { name?: string; staff_member_id?: string }> = {};
  for (const [pid, entry] of Object.entries(proposalByPosition)) {
    const staff_member_id =
      entry.staff_member_id ??
      (entry.name ? resolveStaffMemberIdByName(entry.name, staffMembers) : undefined);
    resolved[pid] = { ...entry, staff_member_id };
  }
  return resolved;
}

function buildCurrentAssignmentsForSuggestions(
  staffRequirements: StaffRequirement[],
  proposalByPosition: Record<string, { staff_member_id?: string }>,
  confirmedByPosition: Record<string, { staff_member_id?: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const req of staffRequirements) {
    const proposalId = proposalByPosition[req.position_id]?.staff_member_id;
    const confirmedId = confirmedByPosition[req.position_id]?.staff_member_id;
    const staffId = proposalId || confirmedId;
    if (staffId) out[req.position_id] = staffId;
  }
  return out;
}

/** Drop reject-only prose for globally excluded staff when they are not on this role in the proposal. */
function sanitizeStaffCorrectionRow(
  row: StaffCorrectionItem,
  proposal: { name?: string; staff_member_id?: string } | undefined,
  rejectedStaff: StaffMemberOption[],
): StaffCorrectionItem {
  let reason = (row.reason ?? '').trim();
  const proposalName = (proposal?.name ?? '').toLowerCase();

  for (const member of rejectedStaff) {
    const fullName = member.name.toLowerCase();
    const firstName = fullName.split(/\s+/)[0] ?? '';
    const proposalHasRejected =
      (proposal?.staff_member_id && member.id === proposal.staff_member_id) ||
      (proposalName &&
        (proposalName.includes(fullName) ||
          (firstName.length >= 3 && proposalName.includes(firstName))));
    if (proposalHasRejected) continue;

    const reasonLower = reason.toLowerCase();
    const mentionsRejected =
      (fullName.length >= 3 && reasonLower.includes(fullName)) ||
      (firstName.length >= 3 && reasonLower.includes(firstName));
    if (!mentionsRejected) continue;

    reason = reason
      .split(/(?<=[.!?])\s+/)
      .map((chunk) => chunk.trim())
      .filter((chunk) => {
        const chunkLower = chunk.toLowerCase();
        if (fullName.length >= 3 && chunkLower.includes(fullName)) return false;
        if (firstName.length >= 3 && chunkLower.includes(firstName)) return false;
        return chunk.length > 0;
      })
      .join(' ')
      .trim();
  }

  return {
    ...row,
    reason: reason || undefined,
  };
}

function sanitizeStaffCorrectionsForProposal(
  rows: StaffCorrectionItem[],
  proposalByPosition: Record<string, { name?: string; staff_member_id?: string }>,
  rejectedStaff: StaffMemberOption[],
): StaffCorrectionItem[] {
  return rows.map((row) =>
    sanitizeStaffCorrectionRow(row, proposalByPosition[row.position_id], rejectedStaff),
  );
}

function correctionsPayloadSignature(payload: SelectionCorrectionsPayload): string {
  return JSON.stringify({
    staff: normalizeStaffCorrections(payload.staff),
    company: normalizeCompanyCorrections(payload.company),
  });
}

interface StaffRequirement {
  position_id: string;
  role_title?: string;
}

interface CompanyBucket {
  bucket_id: string;
  label: string;
}

interface StaffMemberOption {
  id: string;
  name: string;
  department?: string;
  designations?: string[];
}

function collectRejectedStaffMemberIds(
  staffMembers: StaffMemberOption[],
  staffRows: StaffCorrectionItem[],
  correctionModes: Record<string, StaffCorrectionMode>,
  savedRejected?: string[],
): string[] {
  const ids = new Set(savedRejected ?? []);
  for (const row of staffRows) {
    if (row.reject_staff_member_id) ids.add(row.reject_staff_member_id);
  }
  const blob = staffRows
    .filter((row) => (correctionModes[row.position_id] ?? 'none') !== 'none')
    .map((row) => `${row.reason ?? ''} ${freeTextFromList(row.search_hints)}`)
    .join(' ')
    .toLowerCase();
  if (blob.trim()) {
    for (const member of staffMembers) {
      const name = member.name.toLowerCase();
      const first = name.split(/\s+/)[0] ?? '';
      if (name.length >= 3 && blob.includes(name)) ids.add(member.id);
      else if (first.length >= 3 && blob.includes(first)) ids.add(member.id);
    }
  }
  return [...ids];
}

function staffNameForId(
  staffMembers: StaffMemberOption[],
  staffId: string,
): string {
  return staffMembers.find((m) => m.id === staffId)?.name ?? staffId.slice(0, 8);
}

function matchScorePct(score: unknown): number | null {
  const raw = typeof score === 'number' ? score : Number(score);
  if (Number.isNaN(raw)) return null;
  return raw <= 1 ? raw * 100 : raw;
}

function formatStaffOptionLabel(
  staff: {
    name: string;
    department?: string;
    designations?: string[];
    matchPct?: number | null;
  },
): string {
  const parts = [staff.name];
  if (staff.designations?.length) parts.push(staff.designations.join(', '));
  if (staff.department) parts.push(staff.department);
  let label = parts.join(' · ');
  if (staff.matchPct != null && !Number.isNaN(staff.matchPct)) {
    label += ` (${staff.matchPct.toFixed(0)}% match)`;
  }
  return label;
}

export interface SelectionCorrectionsPanelProps {
  reportId: string;
  /** Called after save — e.g. open feedback plan when below target. */
  onSaved?: () => void;
  onOpenRegenPlan?: () => void;
  /** True while draft differs from last saved selection_corrections. */
  onDirtyChange?: (dirty: boolean) => void;
}

export function SelectionCorrectionsPanel({
  reportId,
  onSaved,
  onOpenRegenPlan,
  onDirtyChange,
}: SelectionCorrectionsPanelProps) {
  const queryClient = useQueryClient();
  const { projectId } = useWorkspace();
  const [staffDraft, setStaffDraft] = useState<StaffCorrectionItem[]>([]);
  const [correctionModes, setCorrectionModes] = useState<Record<string, StaffCorrectionMode>>({});
  const [companyDraft, setCompanyDraft] = useState<CompanyCorrectionItem[]>([]);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [suggestedAssignments, setSuggestedAssignments] = useState<SuggestedAssignment[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestionsLoadedRef = useRef(false);
  const staffDraftInitRef = useRef(false);
  const lastDraftInitSignatureRef = useRef('');

  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });

  const { data: correctionsData, isLoading: correctionsLoading } = useQuery({
    queryKey: ['selection-corrections', reportId],
    queryFn: () => getSelectionCorrections(reportId),
    enabled: !!reportId,
  });

  const { data: assignmentsData } = useQuery({
    queryKey: ['report-staff-assignments', reportId],
    queryFn: async () => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      return api.get(`/api/rfp/staff/report-assignments/${reportId}`);
    },
    enabled: !!reportId,
  });

  const { data: v2Content } = useQuery({
    queryKey: ['report-v2-content', reportId],
    queryFn: () => downloadReportV2(reportId),
    enabled: !!reportId,
    staleTime: 60_000,
  });

  const { data: v1Content } = useQuery({
    queryKey: ['report-v1-content', reportId],
    queryFn: () => downloadReportV1(reportId),
    enabled: !!reportId,
    staleTime: 60_000,
  });

  const templateResult = report?.meta?.template_result ?? (report as { metadata?: { template_result?: unknown } })?.metadata?.template_result;
  const staffRequirements: StaffRequirement[] = (templateResult as { staff_requirements?: StaffRequirement[] })?.staff_requirements ?? [];
  const companyReq = (templateResult as { company_requirements?: Record<string, unknown> })?.company_requirements ?? {};

  const { data: staffMembersData } = useQuery({
    queryKey: ['staff-members', projectId],
    queryFn: async () => {
      if (!projectId) return { staff_members: [] as StaffMemberOption[] };
      const entities = await listEntities(projectId, 'staff_member', 1000);
      const staff_members = entities
        .filter((e) => (e.data as Record<string, unknown>)?.name)
        .map((e) => {
          const data = e.data as Record<string, unknown>;
          const designations = data.designations;
          return {
            id: e.id,
            name: String(data.name ?? 'Unknown'),
            department: data.department as string | undefined,
            designations: Array.isArray(designations) ? (designations as string[]) : [],
          };
        });
      return { staff_members };
    },
    enabled: !!projectId && staffRequirements.length > 0,
    staleTime: 120_000,
  });

  const metaStaffAssignments = useMemo(() => {
    const map: Record<string, { staff_name?: string; staff_member_id?: string }> = {};
    const raw = report?.meta?.staff_assignments;
    if (raw && typeof raw === 'object') {
      for (const [pid, val] of Object.entries(raw as Record<string, unknown>)) {
        if (!val || typeof val !== 'object') continue;
        const a = val as Record<string, unknown>;
        map[pid] = {
          staff_name: String(a.staff_name ?? a.name ?? ''),
          staff_member_id: a.staff_member_id ? String(a.staff_member_id) : undefined,
        };
      }
    }
    return map;
  }, [report?.meta?.staff_assignments]);

  const companyBuckets: CompanyBucket[] = useMemo(() => {
    const buckets: CompanyBucket[] = [];
    const caps = (companyReq.capability_statements ?? []) as Array<{ id?: string; topic?: string }>;
    for (const cap of caps) {
      if (cap?.id) {
        buckets.push({ bucket_id: cap.id, label: cap.topic || cap.id });
      }
    }
    const past = (companyReq.past_projects_enumeration ?? []) as Array<{ id?: string; description?: string }>;
    for (const pp of past) {
      if (pp?.id) {
        buckets.push({
          bucket_id: pp.id,
          label: pp.description || pp.id,
        });
      }
    }
    return buckets;
  }, [companyReq]);

  const confirmedByPosition = useMemo(() => {
    const map: Record<string, { staff_name?: string; staff_member_id?: string }> = {
      ...metaStaffAssignments,
    };
    for (const a of assignmentsData?.assignments ?? []) {
      const pid = a.position_id || a.assignment_metadata?.position_id;
      if (pid) {
        map[pid] = { staff_name: a.staff_name, staff_member_id: a.staff_member_id };
      }
    }
    return map;
  }, [assignmentsData, metaStaffAssignments]);

  const proposalByPosition = useMemo(() => {
    const v2Parsed = parseProposalStaffAssignments(v2Content ?? '', staffRequirements);
    const v1Parsed = parseProposalStaffAssignments(v1Content ?? '', staffRequirements);
    return mergeProposalStaffAssignments(v2Parsed, v1Parsed);
  }, [v2Content, v1Content, staffRequirements]);

  const staffMemberOptions = staffMembersData?.staff_members ?? [];

  const proposalByPositionResolved = useMemo(
    () => resolveProposalStaffMemberIds(proposalByPosition, staffMemberOptions),
    [proposalByPosition, staffMemberOptions],
  );

  const proposalContentSignature = useMemo(
    () =>
      staffRequirements
        .map((req) => `${req.position_id}:${proposalByPositionResolved[req.position_id]?.name ?? ''}`)
        .join('|'),
    [staffRequirements, proposalByPositionResolved],
  );

  const currentAssignmentsForSuggestions = useMemo(
    () =>
      buildCurrentAssignmentsForSuggestions(
        staffRequirements,
        proposalByPositionResolved,
        confirmedByPosition,
      ),
    [staffRequirements, proposalByPositionResolved, confirmedByPosition],
  );

  const currentAssignmentsSignature = useMemo(
    () =>
      Object.entries(currentAssignmentsForSuggestions)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([pid, sid]) => `${pid}:${sid}`)
        .join('|'),
    [currentAssignmentsForSuggestions],
  );

  const currentAssignmentNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const req of staffRequirements) {
      if (proposalByPositionResolved[req.position_id]?.staff_member_id) continue;
      const name = proposalByPosition[req.position_id]?.name;
      if (name) out[req.position_id] = name;
    }
    return out;
  }, [staffRequirements, proposalByPosition, proposalByPositionResolved]);

  const suggestedByPosition = useMemo(() => {
    const map: Record<string, SuggestedAssignment> = {};
    for (const s of suggestedAssignments) {
      if (s.position_id) map[s.position_id] = s;
    }
    return map;
  }, [suggestedAssignments]);

  const rejectedStaffIds = useMemo(
    () =>
      collectRejectedStaffMemberIds(
        staffMemberOptions,
        staffDraft,
        correctionModes,
        correctionsData?.selection_corrections?.rejected_staff_member_ids,
      ),
    [staffMemberOptions, staffDraft, correctionModes, correctionsData],
  );

  const rejectedStaffSignature = rejectedStaffIds.slice().sort().join(',');

  useEffect(() => {
    staffDraftInitRef.current = false;
    lastDraftInitSignatureRef.current = '';
    suggestionsLoadedRef.current = false;
    setSuggestedAssignments([]);
    setStaffDraft([]);
    setCorrectionModes({});
    setCompanyDraft([]);
  }, [reportId]);

  useEffect(() => {
    if (!projectId || !templateResult || staffRequirements.length === 0) return;
    let cancelled = false;
    setSuggestionsLoading(true);
    (async () => {
      try {
        const { getAPIClient } = await import('../../api/client');
        const api = getAPIClient();
        const response = (await api.post('/api/rfp/staff/suggest-assignments', {
          project_id: projectId,
          template_result: templateResult,
          report_id: reportId,
          current_assignments: currentAssignmentsForSuggestions,
          current_assignment_names: currentAssignmentNames,
          allow_multi_role: false,
          exclude_staff_member_ids: rejectedStaffIds,
        })) as {
          data?: { suggested_assignments?: SuggestedAssignment[] };
          suggested_assignments?: SuggestedAssignment[];
        };
        if (cancelled) return;
        const list =
          response.data?.suggested_assignments ?? response.suggested_assignments ?? [];
        setSuggestedAssignments(list);
        suggestionsLoadedRef.current = true;
      } catch {
        /* suggestions are optional for display */
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    templateResult,
    staffRequirements.length,
    reportId,
    rejectedStaffSignature,
    currentAssignmentsSignature,
    proposalContentSignature,
  ]);

  useEffect(() => {
    const saved = correctionsData?.selection_corrections;
    if (saved?.company?.length) setCompanyDraft(saved.company);
  }, [correctionsData]);

  useEffect(() => {
    if (correctionsLoading || staffRequirements.length === 0) return;

    const initSignature = `${proposalContentSignature}|${correctionsData?.selection_corrections?.updated_at ?? ''}|${staffMemberOptions.length}`;
    if (staffDraftInitRef.current && lastDraftInitSignatureRef.current === initSignature) {
      return;
    }
    lastDraftInitSignatureRef.current = initSignature;

    const savedStaff = correctionsData?.selection_corrections?.staff;
    let mergedStaff = mergeStaffDraftWithRequirements(staffRequirements, savedStaff);
    mergedStaff = sanitizeStaffCorrectionsForProposal(
      mergedStaff,
      proposalByPositionResolved,
      staffMemberOptions,
    );
    setStaffDraft(mergedStaff);
    setCorrectionModes(correctionModesFromStaffDraft(mergedStaff));
    staffDraftInitRef.current = true;
  }, [
    correctionsLoading,
    correctionsData,
    staffRequirements,
    proposalContentSignature,
    staffMemberOptions,
  ]);

  useEffect(() => {
    if (companyDraft.length > 0 || companyBuckets.length === 0) return;
    setCompanyDraft(
      companyBuckets.map((b) => ({
        bucket_id: b.bucket_id,
        action: 'replace_projects',
        reject_project_labels: [],
        pin_entity_ids: [],
        pin_artifact_ids: [],
        search_hints: [],
        reason: '',
      })),
    );
  }, [companyBuckets, companyDraft.length]);

  const saveMutation = useMutation({
    mutationFn: (payload: SelectionCorrectionsPayload) =>
      putSelectionCorrections(reportId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selection-corrections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report-v2-content', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report-v1-content', reportId] });
      setSaveMessage('Corrections saved and staff slate synced.');
      onSaved?.();
    },
  });

  const updateStaff = (index: number, patch: Partial<StaffCorrectionItem>) => {
    setStaffDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const setCorrectionMode = (positionId: string, mode: StaffCorrectionMode) => {
    setCorrectionModes((prev) => ({ ...prev, [positionId]: mode }));
    setStaffDraft((prev) =>
      prev.map((row) => {
        if (row.position_id !== positionId) return row;
        if (mode === 'none') {
          return { ...row, assign_staff_member_id: undefined, reason: '', search_hints: [] };
        }
        if (mode === 'specific') {
          return { ...row, search_hints: [] };
        }
        return { ...row, assign_staff_member_id: undefined };
      }),
    );
  };

  const buildStaffPayload = (): StaffCorrectionItem[] =>
    staffDraft
      .map((row) => {
        const mode = correctionModes[row.position_id] ?? 'none';
        if (mode === 'none') return null;
        if (mode === 'specific') {
          if (!row.assign_staff_member_id) return null;
          return {
            ...row,
            action: 'replace',
            search_hints: [],
          };
        }
        const hints = freeTextToList(freeTextFromList(row.search_hints));
        if (!hints.length && !row.reason?.trim()) return null;
        return {
          ...row,
          action: 'search',
          assign_staff_member_id: undefined,
          search_hints: hints,
        };
      })
      .filter((row): row is StaffCorrectionItem => row != null);

  const buildCompanyPayload = (): CompanyCorrectionItem[] =>
    companyDraft.map((row) => ({
      ...row,
      reject_project_labels: commaSeparatedToList(
        commaSeparatedFromList(row.reject_project_labels),
      ),
      pin_artifact_ids: commaSeparatedToList(
        commaSeparatedFromList(row.pin_artifact_ids),
      ),
      search_hints: freeTextToList(freeTextFromList(row.search_hints)),
    }));

  const draftPayload = useMemo(
    (): SelectionCorrectionsPayload => ({
      staff: buildStaffPayload(),
      company: buildCompanyPayload(),
    }),
    [staffDraft, correctionModes, companyDraft],
  );

  useEffect(() => {
    if (!onDirtyChange || !staffDraftInitRef.current) return;
    const saved = correctionsData?.selection_corrections;
    const savedPayload: SelectionCorrectionsPayload = {
      staff: saved?.staff ?? [],
      company: saved?.company ?? [],
    };
    onDirtyChange(
      correctionsPayloadSignature(draftPayload) !== correctionsPayloadSignature(savedPayload),
    );
  }, [draftPayload, correctionsData, onDirtyChange]);

  const updateCompany = (index: number, patch: Partial<CompanyCorrectionItem>) => {
    setCompanyDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const buildStaffSelectOptions = (positionId: string) => {
    const suggested = suggestedByPosition[positionId];
    const options: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();

    if (suggested?.staff_member_id) {
      seen.add(suggested.staff_member_id);
      options.push({
        value: suggested.staff_member_id,
        label: `${formatStaffOptionLabel({
          name: suggested.staff_name ?? suggested.staff_member_id.slice(0, 8),
          department: suggested.staff_department,
          designations: suggested.staff_designations,
          matchPct: matchScorePct(suggested.match_score),
        })} (suggested)`,
      });
    }

    for (const member of staffMemberOptions) {
      if (seen.has(member.id) || rejectedStaffIds.includes(member.id)) continue;
      seen.add(member.id);
      options.push({
        value: member.id,
        label: formatStaffOptionLabel(member),
      });
    }

    return options;
  };

  if (reportLoading || correctionsLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-theme-text-secondary">
        <LoadingSpinner size="sm" />
        Loading selection corrections…
      </div>
    );
  }

  if (!staffRequirements.length && !companyBuckets.length) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-theme-border bg-gray-50/80 dark:bg-gray-900/20 p-4 space-y-4"
      data-testid="selection-corrections-panel"
    >
      <div>
        <h4 className="text-sm font-semibold text-theme-text flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Selection corrections
        </h4>
        <p className="mt-1 text-xs text-theme-text-secondary">
          Tell the system when assigned people or cited projects are wrong, and where to look instead.
          Saved corrections feed the QA regen plan and the next re-score.
        </p>
      </div>

      {staffRequirements.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-theme-text-secondary flex items-center gap-1">
            <Users className="h-3.5 w-3.5" /> Staff
            {suggestionsLoading && (
              <span className="font-normal normal-case text-theme-text-muted">(loading suggestions…)</span>
            )}
          </p>
          {staffDraft.length === 0 && (
            <p className="text-xs text-theme-text-muted">No staff positions in this proposal template.</p>
          )}
          {rejectedStaffIds.length > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/20 px-2 py-1.5">
              Excluded from all roles based on your corrections:{' '}
              {rejectedStaffIds.map((id) => staffNameForId(staffMemberOptions, id)).join(', ')}
            </p>
          )}
          {staffDraft.map((row, idx) => {
            const proposal = proposalByPositionResolved[row.position_id] ?? proposalByPosition[row.position_id];
            const suggested = suggestedByPosition[row.position_id];
            const confirmed = confirmedByPosition[row.position_id];
            const suggestedPct = suggested ? matchScorePct(suggested.match_score) : null;
            const matchSummary = suggested ? formatStaffMatchSummary(suggested, 180) : '';
            const mode = correctionModes[row.position_id] ?? 'none';
            const selectOptions = buildStaffSelectOptions(row.position_id);
            const proposalRejected =
              proposal?.staff_member_id &&
              rejectedStaffIds.includes(proposal.staff_member_id);
            const suggestedRejected =
              suggested?.staff_member_id &&
              rejectedStaffIds.includes(suggested.staff_member_id);
            const suggestionMatchesProposal =
              !!proposal?.name &&
              !!suggested?.staff_name &&
              (proposal.staff_member_id === suggested.staff_member_id ||
                proposal.name.toLowerCase().includes(suggested.staff_name.toLowerCase()) ||
                suggested.staff_name.toLowerCase().includes(proposal.name.split(',')[0]?.trim().toLowerCase() ?? ''));
            const suggestionLabel =
              (suggested as SuggestedAssignment & { is_current_assignment?: boolean })
                ?.is_current_assignment || suggestionMatchesProposal
                ? 'Current assignment'
                : 'System suggestion';

            return (
              <div
                key={row.position_id}
                className="grid gap-3 rounded-md border border-theme-border bg-white dark:bg-gray-950/40 p-3"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-theme-text">
                    {row.role_title || row.position_id}
                  </p>
                  {proposal?.name && (
                    <p className="text-xs text-theme-text-secondary">
                      In proposal:{' '}
                      <span className="font-medium text-theme-text">{proposal.name}</span>
                    </p>
                  )}
                  {suggested && (
                    <p className="text-xs text-theme-text-secondary">
                      {suggestionLabel}:{' '}
                      <span className="font-medium text-theme-text">
                        {suggested.staff_name ?? suggested.staff_member_id.slice(0, 8)}
                      </span>
                      {suggestedPct != null && (
                        <span> ({suggestedPct.toFixed(0)}% match)</span>
                      )}
                    </p>
                  )}
                  {matchSummary && (
                    <p className="text-xs text-theme-text-muted" title={matchSummary}>
                      Match breakdown: {matchSummary}
                    </p>
                  )}
                  {suggestedRejected && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      System suggestion is excluded by your corrections — save and re-run regen
                      to refresh assignments.
                    </p>
                  )}
                  {proposalRejected && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Proposal still lists an excluded person for this role until regen completes.
                    </p>
                  )}
                  {confirmed?.staff_name && (
                    <p className="text-xs text-theme-text-secondary">
                      Confirmed in slate:{' '}
                      <span className="font-medium text-theme-text">{confirmed.staff_name}</span>
                    </p>
                  )}
                </div>

                <fieldset className="space-y-2">
                  <legend className="sr-only">Correction type for {row.role_title}</legend>
                  <div className="flex flex-col gap-1.5 text-xs">
                    {(
                      [
                        ['none', 'No correction'],
                        ['specific', 'Replace with a specific person'],
                        ['search', 'Find someone with certain skills'],
                      ] as const
                    ).map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`correction-mode-${row.position_id}`}
                          checked={mode === value}
                          onChange={() => setCorrectionMode(row.position_id, value)}
                          className="shrink-0"
                        />
                        <span className="text-theme-text">{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {mode === 'specific' && (
                  <div className="space-y-2">
                    <select
                      value={row.assign_staff_member_id ?? ''}
                      onChange={(e) =>
                        updateStaff(idx, {
                          assign_staff_member_id: e.target.value || undefined,
                        })
                      }
                      className="w-full rounded border border-theme-border bg-white dark:bg-gray-950 px-2 py-1.5 text-xs text-theme-text"
                      aria-label={`Replace staff for ${row.role_title || row.position_id}`}
                    >
                      <option value="">Select staff member…</option>
                      {selectOptions
                        .filter((opt) => opt.value !== '')
                        .map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                    </select>
                    <Input
                      placeholder="Why this person instead? (optional)"
                      value={row.reason ?? ''}
                      onChange={(e) => updateStaff(idx, { reason: e.target.value })}
                      className="text-xs"
                    />
                  </div>
                )}

                {mode === 'search' && (
                  <div className="space-y-2">
                    <Input
                      placeholder="Skills, designations, or experience to look for"
                      value={freeTextFromList(row.search_hints)}
                      onChange={(e) =>
                        updateStaff(idx, {
                          search_hints: e.target.value ? [e.target.value] : [],
                        })
                      }
                      className="text-xs"
                    />
                    <Input
                      placeholder="Why the current pick is wrong / what to prioritize"
                      value={row.reason ?? ''}
                      onChange={(e) => updateStaff(idx, { reason: e.target.value })}
                      className="text-xs"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {companyBuckets.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-theme-text-secondary flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5" /> Past projects &amp; capabilities
          </p>
          {companyDraft.map((row, idx) => {
            const label = companyBuckets.find((b) => b.bucket_id === row.bucket_id)?.label;
            return (
              <div
                key={row.bucket_id}
                className="grid gap-2 rounded-md border border-theme-border bg-white dark:bg-gray-950/40 p-3 md:grid-cols-2"
              >
                <div>
                  <p className="text-sm font-medium text-theme-text">{label || row.bucket_id}</p>
                  <p className="text-xs text-theme-text-secondary">{row.bucket_id}</p>
                </div>
                <div className="space-y-2">
                  <Input
                    placeholder="Reject project labels (comma-separated)"
                    value={commaSeparatedFromList(row.reject_project_labels)}
                    onChange={(e) =>
                      updateCompany(idx, {
                        reject_project_labels: e.target.value ? [e.target.value] : [],
                      })
                    }
                    className="text-xs"
                  />
                  <Input
                    placeholder="Pin artifact IDs (comma-separated UUIDs)"
                    value={commaSeparatedFromList(row.pin_artifact_ids)}
                    onChange={(e) =>
                      updateCompany(idx, {
                        pin_artifact_ids: e.target.value ? [e.target.value] : [],
                      })
                    }
                    className="text-xs"
                  />
                  <Input
                    placeholder="Where to look — describe what evidence to find"
                    value={freeTextFromList(row.search_hints)}
                    onChange={(e) =>
                      updateCompany(idx, {
                        search_hints: e.target.value ? [e.target.value] : [],
                      })
                    }
                    className="text-xs"
                  />
                  <Input
                    placeholder="Reason / notes"
                    value={row.reason ?? ''}
                    onChange={(e) => updateCompany(idx, { reason: e.target.value })}
                    className="text-xs"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={saveMutation.isPending}
          onClick={() => {
            setSaveMessage(null);
            saveMutation.mutate(draftPayload);
          }}
        >
          <Save className="h-4 w-4 mr-1" />
          Save corrections
        </Button>
        {onOpenRegenPlan && (
          <Button type="button" variant="ghost" size="sm" onClick={onOpenRegenPlan}>
            Apply via regen plan
          </Button>
        )}
        {saveMessage && (
          <span className="text-xs text-emerald-700 dark:text-emerald-300">{saveMessage}</span>
        )}
        {saveMutation.isError && (
          <span className="text-xs text-red-600">
            {(saveMutation.error as Error)?.message || 'Save failed'}
          </span>
        )}
      </div>
    </div>
  );
}

export default SelectionCorrectionsPanel;
